import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import type { QueryClient } from '@tanstack/react-query';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import { ConnectorCatalogService } from './connector-catalog.service';
import type { ConnectorCatalogEntry } from './connector-catalog.service';
import { ConnectorConnectionsService } from './connector-connections.service';
import { Connectors } from './connectors';
import type {
  ConnectPayload,
  ConnectResult,
  ConnectorRef,
  DisconnectPayload,
  DisconnectResult,
  GetConnectorOptions,
  IConnector,
  QueryConnectorsOptions,
} from './types';

const key = (appId: string, compId: string): string => `${appId}:${compId}`;

export class ConnectorService {
  private readonly catalog: ConnectorCatalogService;
  private readonly connections: ConnectorConnectionsService;

  constructor(
    rpcClient?: AmbassadorBootstrap,
    httpClient?: IHttpClient,
    queryClient?: QueryClient,
    errorHandler?: ErrorHandlerPublicAPI,
  ) {
    this.catalog = new ConnectorCatalogService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    // No query client: the proxy answers by the caller's own site or account,
    // so a cache shared across hosts would serve one context's connection state
    // to another, and the consent must re-read the state after a connect.
    this.connections = new ConnectorConnectionsService(
      rpcClient,
      httpClient,
      undefined,
      errorHandler,
    );
  }

  public async getConnector({
    appId,
    compId,
    languageCode,
  }: GetConnectorOptions): Promise<ConnectorCatalogEntry | undefined> {
    const [entry] = await this.catalog.getCatalogByAppIds({
      appIds: [appId],
      componentIds: [compId],
      languageCode,
    });
    return entry;
  }

  public async isConnected(ref: ConnectorRef): Promise<boolean> {
    const connected = await this.connections.filterConnected([ref]);
    return connected.length > 0;
  }

  public async query(
    options: QueryConnectorsOptions = {},
  ): Promise<Connectors> {
    const { filter = 'ALL', paging = {}, connectorRefs } = options;

    if (connectorRefs) {
      return this.queryByRefs(connectorRefs, filter);
    }

    return filter === 'CONNECTED'
      ? this.queryConnectedPage(paging)
      : this.queryCatalogPage(filter, paging);
  }

  // Fetches connector data for specific app+component pairs. The catalog is
  // narrowed to the requested components, so we only enrich the returned entries
  // with their connection state and apply the requested connected/disconnected filter.
  private async queryByRefs(
    connectorRefs: ConnectorRef[],
    filter: QueryConnectorsOptions['filter'],
  ): Promise<Connectors> {
    if (connectorRefs.length === 0) {
      return new Connectors([], { hasNext: false, total: 0 });
    }

    const appIds = connectorRefs.map((ref) => ref.appId);
    const componentIds = connectorRefs.map((ref) => ref.compId);

    const entries = await this.catalog.getCatalogByAppIds({
      appIds,
      componentIds,
    });
    const connectedKeys = new Set(
      (await this.connections.filterConnected(connectorRefs)).map((ref) =>
        key(ref.appId, ref.compId),
      ),
    );

    let connectors: IConnector[] = entries.map((entry) => ({
      ...entry,
      isConnected: connectedKeys.has(key(entry.appId, entry.compId)),
    }));

    if (filter === 'CONNECTED') {
      connectors = connectors.filter((c) => c.isConnected);
    } else if (filter === 'DISCONNECTED') {
      connectors = connectors.filter((c) => !c.isConnected);
    }

    return new Connectors(connectors, {
      hasNext: false,
      total: connectors.length,
    });
  }

  private async queryConnectedPage({
    limit,
    cursor,
  }: {
    limit?: number;
    cursor?: string;
  }): Promise<Connectors> {
    const [catalog, page] = await Promise.all([
      this.catalog.getCatalog(),
      this.connections.queryConnections({ limit, cursor }),
    ]);

    const catalogByKey = new Map<string, ConnectorCatalogEntry>(
      catalog.map((entry) => [key(entry.appId, entry.compId), entry]),
    );

    const connectors: IConnector[] = page.connectors
      .map((c) => catalogByKey.get(key(c.appId, c.compId)))
      .filter((entry): entry is ConnectorCatalogEntry => Boolean(entry))
      .map((entry) => ({ ...entry, isConnected: true }));

    return new Connectors(connectors, {
      hasNext: page.hasNext,
      nextCursor: page.nextCursor,
    });
  }

  private async queryCatalogPage(
    filter: QueryConnectorsOptions['filter'],
    { limit, cursor }: { limit?: number; cursor?: string },
  ): Promise<Connectors> {
    const page = await this.catalog.getCatalogPage({ limit, cursor });

    const connectedKeys = new Set(
      (await this.connections.filterConnected(page.entries)).map((ref) =>
        key(ref.appId, ref.compId),
      ),
    );

    let connectors: IConnector[] = page.entries.map((entry) => ({
      ...entry,
      isConnected: connectedKeys.has(key(entry.appId, entry.compId)),
    }));

    if (filter === 'DISCONNECTED') {
      connectors = connectors.filter((c) => !c.isConnected);
    }

    return new Connectors(connectors, {
      hasNext: page.hasNext,
      nextCursor: page.hasNext ? page.cursor : undefined,
    });
  }

  public async connect(payload: ConnectPayload): Promise<ConnectResult> {
    return this.connections.startConnect(payload);
  }

  public async disconnect({
    compId,
    appId,
  }: DisconnectPayload): Promise<DisconnectResult> {
    try {
      const connectorIds = await this.connections.findActiveConnectorIds(
        appId,
        compId,
      );
      if (connectorIds.length === 0) {
        return { succeed: false };
      }
      await Promise.all(
        connectorIds.map((connectorId) =>
          this.connections.deleteConnector(connectorId),
        ),
      );
      return { succeed: true };
    } catch (error) {
      return { succeed: false, error };
    }
  }
}
